import { removeNullsDeep } from "@/api/app";
import { ExtensionType } from "@/api/enums/ExtensionType";
import { RegistrationType } from "@/api/enums/RegistrationType";
import { RenewingType } from "@/api/enums/RenewingType";
import ExamDateCombobox from "@/components/molecules/ExamDateCombobox";
import { extensionPropertiesFromDto } from "@/components/molecules/RegistrationForms/ExtensionProperties/extension-properties.schema";
import ExtensionPropertiesForm from "@/components/molecules/RegistrationForms/ExtensionProperties/ExtensionPropertiesForm";
import InitialPropertiesForm from "@/components/molecules/RegistrationForms/InitialProperties/InitialPropertiesForm";
import RenewingPropertiesForm from "@/components/molecules/RegistrationForms/RenewingProperties/RenewingPropertiesForm";
import { Perimeter } from "@/lib/perimeter";
import { Form } from "@atoms/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import _ from "lodash";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router";
import NextStepButton from "../NextStepButtons";
import { RegisterContext } from "../RegisterContext";
import {
  examSelectionOf,
  RegisterStepSchema,
  registerStepSchema,
  registerStepSchemaToExamOptions,
} from "./register-step.schema";
import RegistrationTypeRadio from "./RegistrationTypeRadio";
import WarningMessage from "./WarningMessage";
import {
  AvailableExamsError,
  useAvailableExams,
} from "@/hooks/useAvailableExams";
import { useCandidateCertificates } from "@/hooks/useCandidateCertificates";
import { isWithinRenewalWindow } from "@/lib/renewal-window";

const examsErrorLabel: Record<AvailableExamsError, string> = {
  exams: "Erreur de chargement des dates d'examen",
  certificates: "Erreur de chargement des certificats",
};

const RegisterStep = () => {
  const {
    changeDraft,
    onNextStep,
    registrationDraft: draft,
    pending,
    candidateId,
  } = useOutletContext<RegisterContext>();

  const form = useForm<RegisterStepSchema>({
    resolver: zodResolver(registerStepSchema),
    defaultValues: _.merge(
      {
        type: undefined,
        initialExamId: -1,
        renewingExamId: -1,
        extensionExamId: -1,
        initialProperties: {
          utc: 1,
          perimeter: Perimeter.empty(),
        },
        renewingProperties: {
          type: RegistrationType.RENEW,
          perimeter: Perimeter.empty(),
          renewingCertificatesIDs: [],
          renewingType: RenewingType.SAME,
        },
        extensionProperties: {
          type: RegistrationType.EXTENSION,
          utc: 1,
          certificateId: -1,
          extension: {
            extensionType: ExtensionType.MERCHANDISES,
            extendedMerchandiseClasses: [],
            extendedTransportModes: [],
          },
        },
      },
      removeNullsDeep({
        type: draft.type,
        initialExamId:
          draft.type === RegistrationType.INITIAL ? draft.examId : -1,
        renewingExamId:
          draft.type === RegistrationType.RENEW ? draft.examId : -1,
        extensionExamId:
          draft.type === RegistrationType.EXTENSION ? draft.examId : -1,
        initialProperties: draft.initialProperties,
        renewingProperties: draft.renewingProperties,
        extensionProperties: draft.extensionProperties
          ? extensionPropertiesFromDto(draft.extensionProperties)
          : undefined,
      }),
    ),
  });

  const registrationType = form.watch("type");

  const { exams, registrableExams, isLoading, error } = useAvailableExams({
    registrationType,
    candidateId,
  });
  const { selectableCertificates } = useCandidateCertificates(candidateId);

  const onSubmit = (data: RegisterStepSchema) => {
    const { fieldName, examId } = examSelectionOf(data);

    if (registrableExams && !registrableExams.some(({ id }) => id === examId)) {
      form.setError(fieldName, {
        message:
          "Cet examen n'est plus ouvert aux inscriptions, veuillez en choisir un autre.",
      });
      return;
    }

    if (data.type === RegistrationType.RENEW && selectableCertificates) {
      const chosenCertificates = selectableCertificates.filter(({ id }) =>
        data.renewingProperties.renewingCertificatesIDs.includes(id),
      );

      if (
        chosenCertificates.length !==
        data.renewingProperties.renewingCertificatesIDs.length
      ) {
        form.setError("renewingProperties.renewingCertificatesIDs", {
          message:
            "Un certificat sélectionné n'est plus valide, veuillez revoir votre sélection.",
        });
        return;
      }

      const chosenExam = registrableExams?.find(({ id }) => id === examId);

      if (
        chosenExam &&
        chosenCertificates.length > 0 &&
        !chosenCertificates.some((certificate) =>
          isWithinRenewalWindow(
            new Date(chosenExam.date),
            new Date(certificate.expirationDate),
          ),
        )
      ) {
        form.setError(fieldName, {
          message:
            "Cet examen n'est pas dans les 12 mois précédant l'expiration des certificats sélectionnés.",
        });
        return;
      }
    }

    changeDraft(registerStepSchemaToExamOptions(data));
  };

  return (
    <div className="flex flex-col items-start w-full max-w-4xl">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => onSubmit(data))}
          className="flex w-full flex-col between:mt-8"
        >
          <div className="flex p-4 flex-col items-start gap-8 self-stretch rounded-md border-gray-200 bg-white border w-full">
            <h1>Inscription à l'examen</h1>
            <RegistrationTypeRadio candidateId={candidateId} />

            {registrationType === RegistrationType.INITIAL && (
              <>
                <ExamDateCombobox
                  exams={exams}
                  isLoading={isLoading}
                  isError={!!error}
                  errorLabel={error && examsErrorLabel[error]}
                  fieldName="initialExamId"
                  allowReportingAfterRegistrationEnded={!!candidateId}
                />
                <InitialPropertiesForm fieldName="initialProperties" />
              </>
            )}

            {registrationType === RegistrationType.RENEW && (
              <>
                <ExamDateCombobox
                  exams={exams}
                  isLoading={isLoading}
                  isError={!!error}
                  errorLabel={error && examsErrorLabel[error]}
                  fieldName="renewingExamId"
                  allowReportingAfterRegistrationEnded={!!candidateId}
                />
                <RenewingPropertiesForm
                  fieldName="renewingProperties"
                  candidateId={candidateId}
                />
              </>
            )}

            {registrationType === RegistrationType.EXTENSION && (
              <>
                <ExamDateCombobox
                  exams={exams}
                  isLoading={isLoading}
                  isError={!!error}
                  errorLabel={error && examsErrorLabel[error]}
                  fieldName="extensionExamId"
                  allowReportingAfterRegistrationEnded={!!candidateId}
                />
                <ExtensionPropertiesForm
                  fieldName="extensionProperties"
                  candidateId={candidateId}
                />
              </>
            )}

            <WarningMessage />
          </div>
          <NextStepButton
            onNext={form.handleSubmit((data) => onSubmit(data))}
            onPrevious={() => onNextStep(false)}
            pending={pending}
          />
        </form>
      </Form>
    </div>
  );
};

export default RegisterStep;
